import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { useCases } from '../../infrastructure/di/container';
import { getErrorMessage } from '../../infrastructure/http/common';
import {
  DEFAULT_DISCOVERED_CHANNEL_FILTERS,
  DiscoveredChannelEntity,
  DiscoveredChannelFilters,
} from '../../domain/entities/DiscoveredChannel';

const DEFAULT_PER_PAGE = 50;

const toErrorSlug = (e: unknown): string => {
  if (isAxiosError(e)) return getErrorMessage(e);
  if (e instanceof Error && e.message) return e.message;
  return 'unknown-error';
};

interface DiscoveredChannelsState {
  items: DiscoveredChannelEntity[];
  total: number;
  page: number;
  perPage: number;
  filters: DiscoveredChannelFilters;
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  load: () => Promise<void>;
  setFilters: (filters: DiscoveredChannelFilters) => Promise<void>;
  setPage: (page: number, perPage: number) => Promise<void>;
  importToCatalog: (channels: DiscoveredChannelEntity[]) => Promise<string>;
  reject: (channels: DiscoveredChannelEntity[]) => Promise<void>;
}

export const useDiscoveredChannelsStore = create<DiscoveredChannelsState>((set, get) => {
  const mutate = async <T>(action: () => Promise<T>): Promise<T> => {
    set({ isSaving: true });
    try {
      return await action();
    } catch (e) {
      throw new Error(toErrorSlug(e));
    } finally {
      set({ isSaving: false });
    }
  };

  return {
    items: [],
    total: 0,
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    filters: DEFAULT_DISCOVERED_CHANNEL_FILTERS,
    isLoading: false,
    isSaving: false,
    async load() {
      const { filters, page, perPage } = get();
      set({ isLoading: true, error: undefined });
      try {
        const result = await useCases.listDiscoveredChannels().execute({ filters, page, perPage });
        set({ items: result.items, total: result.total, isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: toErrorSlug(e) });
      }
    },
    async setFilters(filters) {
      set({ filters, page: 1 });
      await get().load();
    },
    async setPage(page, perPage) {
      set({ page, perPage });
      await get().load();
    },
    importToCatalog(channels) {
      return mutate(() => useCases.importDiscoveredChannels().execute(channels));
    },
    async reject(channels) {
      await mutate(() => useCases.rejectDiscoveredChannels().execute(channels));
      await get().load();
    },
  };
});
